/**
 * `/help` — in-app help center (AUTONOMY · Layer 3).
 *
 * Guides index by role with client-side search over titles, summaries and
 * sections. The content lives in `features/help/guides/*` (self-describing
 * registry with a completeness test).
 */
import { createRoute } from "@tanstack/solid-router";
import { createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { LifeBuoy, Search } from "@fleet/icons";

import { appRoute } from "./route";
import { PageContainer, PageHeader } from "@fleet/ui";
import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { HELP_GUIDES } from "@/features/help/registry";

function HelpIndexPage() {
  const { t } = useI18n();
  const [query, setQuery] = createSignal("");

  // Simple client-side search: a guide is visible if its title/description or
  // any of its sections (title/summary) contains the text.
  const normalized = () => query().trim().toLocaleLowerCase();
  const results = createMemo(() => {
    const q = normalized();
    if (!q) {
      return HELP_GUIDES.map((guide) => ({ guide, sections: guide.sections }));
    }
    return HELP_GUIDES.map((guide) => {
      const guideHit =
        t(guide.title).toLocaleLowerCase().includes(q) ||
        t(guide.description).toLocaleLowerCase().includes(q);
      const sections = guide.sections.filter(
        (s) =>
          t(s.title).toLocaleLowerCase().includes(q) ||
          t(s.summary).toLocaleLowerCase().includes(q),
      );
      return guideHit ? { guide, sections: guide.sections } : { guide, sections };
    }).filter((r) => r.sections.length > 0);
  });

  return (
    <PageContainer maxWidth="4xl">
      <div class="space-y-6 p-4 md:p-6">
        <PageHeader
          icon={LifeBuoy}
          title={t(msg`Help center`)}
          description={t(
            msg`Role-oriented guides for everything the platform can do — every screen and setting is documented here, inside the app.`,
          )}
        />

        <label class="relative block">
          <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query()}
            onInput={(e) => setQuery(e.currentTarget.value)}
            placeholder={t(msg`Search the guides…`)}
            data-testid="help-search"
            class="w-full rounded-2xl border border-border bg-surface/40 py-2.5 pl-9 pr-3 text-sm outline-none transition-colors focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
          />
        </label>

        <Show
          when={results().length > 0}
          fallback={
            <p
              class="py-10 text-center text-sm text-muted-foreground"
              data-testid="help-no-results"
            >
              {t(msg`No guide matches your search.`)}
            </p>
          }
        >
          <div class="grid gap-4 sm:grid-cols-2">
            <For each={results()}>
              {({ guide, sections }) => (
                <Link
                  to="/help/$guideSlug"
                  params={{ guideSlug: guide.slug }}
                  data-testid={`help-guide-${guide.slug}`}
                  class="group flex flex-col gap-2 rounded-2xl border border-border bg-surface/40 p-4 transition-all hover:border-primary/40 hover:shadow-md"
                >
                  <div class="flex items-center gap-2.5">
                    <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <guide.icon class="h-5 w-5" />
                    </span>
                    <div class="min-w-0">
                      <h2 class="truncate text-sm font-bold text-foreground">{t(guide.title)}</h2>
                      <p class="truncate text-xs text-muted-foreground">{t(guide.audience)}</p>
                    </div>
                  </div>
                  <p class="text-xs leading-relaxed text-muted-foreground">
                    {t(guide.description)}
                  </p>
                  <Show when={normalized()}>
                    <ul class="mt-1 space-y-0.5">
                      <For each={sections.slice(0, 4)}>
                        {(s) => <li class="truncate text-xs text-primary">• {t(s.title)}</li>}
                      </For>
                    </ul>
                  </Show>
                </Link>
              )}
            </For>
          </div>
        </Show>
      </div>
    </PageContainer>
  );
}

// TODO(nav): once the new nav-model lands (agent `nav`, routes/_app/
// route.tsx), add the "Ayuda" entry → `/help` (LifeBuoy icon, testid
// nav-help). This agent doesn't touch route.tsx (nav lane); in the meantime
// help is reachable via URL and via the contextual "?" buttons.
export const helpRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/help",
  component: HelpIndexPage,
});
